import { useMsal } from '@azure/msal-react';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../store';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { SortHelper } from '../SortHelper';
import { DataHelpers } from '../DataHelpers';
import { useTranslation } from 'react-i18next';
import { TokenHelper } from '../TokenHelper';
import { useMediaQuery } from '../DeviceInfo';
import { Consultant, IsNeomyticConsultant } from '../interfaces/Consultant';
import { ConsultantActionCreators } from '../store/consultants';
import { dataActionCreators, DataState, Product } from '../store/Data';
import { TeamsState } from '../store/TeamsState';
import { UserLoginState } from '../store/Login';
import { Avatar, Badge, Body1Stronger, Button, Caption1, Divider, Dropdown, makeStyles, mergeClasses, Option, Skeleton, SkeletonItem, Spinner, Text, Title3, tokens } from '@fluentui/react-components';
import { useEditorStyles, useTabStyles } from './Styles/Editor';
import { GotoDirectoryPeppolWeb } from '../API/PeppolDirectory';
import { useCustomers, useConsultants, useLoggedConsultant, useArticles, useConsultantsInvoices, useFixePriceConsultants, useInvoices, useProducts, useProjectsAssigments, useSales, useTimeSheetEntries } from './Tools/ObjectLoader';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

export const useGridStyles = makeStyles({
    FlexHomeNonAdmin: {
        display: "flex",
        flexDirection: "column",
        gap: tokens.spacingVerticalM,
    },
    FlexHomeAdmin: {
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
        overflowY: "auto",
        overflowX: "hidden",
    },
    GridPeriodeView: {
        display: "grid",
        gridTemplateColumns: "16% 21% 21% 21% 21%",
        justifyContent: "space-evenly",
    },
    GridPeriodeViewSmall: {
        display: "grid",
        gridTemplateColumns: "16% 28% 28% 28%",
        justifyContent: "space-evenly",
    },
    GridPeriodeViewOfConsultants: {
        display: "grid",
        gridTemplateColumns: "9% 7% 7% 7% 7% 7% 7% 7% 7% 7% 7% 7% 7% 7%",
        justifyContent: "space-evenly",
        width: "100%",
        alignItems: "center",
    },
    GridPeriodeViewOfConsultantsSmall: {
        display: "grid",
        gridTemplateColumns: "70% 30%",
        justifyContent: "space-evenly",
        width: "100%",
        alignItems: "center",
    },
});

// https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/migration-guide.md

interface CountElement {
    key: number,
    month: number,
    year: number,
    revenue: number,
    salesRevenue: number,
    otherRevenue: number,   // free invoice entries
    cost: number,
    isActif: boolean,
}

interface ConsultantResume {
    consultant: Consultant,
    e: CountElement[]
    revenue: number,
    cost: number,
}

interface ProductResume {
    product: Product,
    e: CountElement[]
    revenue: number,
}

//const Number_Desc = (a: CountElement, b: CountElement): number => {
//    if (a.key < b.key) {
//        return 1;
//    }
//    if (a.key > b.key) {
//        return -1;
//    }
//    return 0;
//};

export const Home = () => {
    const UserLoginState = useSelector<ApplicationState, UserLoginState>((state: ApplicationState) => state.userNeomyticLogin);
    const viewAdminInfo = useSelector((state: ApplicationState) => state.userNeomyticLogin.viewAdminInfo);
    if (UserLoginState.rigths && UserLoginState.rigths.isAdmin && viewAdminInfo) {
        return <HomeAdmin />;
    }
    return <HomeNonAdmin />;
};
export const HomeNonAdmin = () => {
    const { consultants, consultantsLoading } = useConsultants();
    const { instance } = useMsal();
    const dispatch = useDispatch();
    const teamsState = useSelector<ApplicationState, TeamsState>((state: ApplicationState) => state.teamsState);
    const { t } = useTranslation();
    const stylesTab = useTabStyles();
    const stylesGrid = useGridStyles();
    const UserLoginState = useSelector<ApplicationState, UserLoginState>((state: ApplicationState) => state.userNeomyticLogin);
    const consultantsPhotoLoaded = useSelector((state: ApplicationState) => state.consultantsState.consultantsPhotoLoaded);
    const styles = useEditorStyles();
    const { loggedConsultant } = useLoggedConsultant();

    React.useEffect(() => {
        const consultantToCheck = consultants && consultants.length === 1
            ? consultants[0]
            : (consultants && UserLoginState.rigths && UserLoginState.rigths.consultantid
                ?
                consultants.find(c => UserLoginState.rigths && UserLoginState.rigths.consultantid !== undefined && c.consultantid === UserLoginState.rigths.consultantid)
                :
                null
            );
        if (consultantToCheck && consultantToCheck.peppolStatus === undefined) {
            dispatch(ConsultantActionCreators.requestConsultantsPeppolStatus(instance, teamsState, consultantToCheck));
        }
        if (consultants && !consultantsLoading && consultantsPhotoLoaded === false) {
            TokenHelper.GetGraphToken(instance, teamsState, UserLoginState.graphToken)
                .then(token => {
                    if (token) dispatch(ConsultantActionCreators.requestConsultantsPhoto(token));
                });
        }
    }, [consultants]);

    React.useEffect(() => {
        const consultantQuickView = consultants && consultants.length === 1
            ? consultants[0]
            : (consultants && UserLoginState.rigths && UserLoginState.rigths.consultantid
                ?
                consultants.find(c => UserLoginState.rigths && UserLoginState.rigths.consultantid !== undefined && c.consultantid === UserLoginState.rigths.consultantid)
                :
                null
            );
        if (consultantQuickView && consultantQuickView.peppolStatus === undefined) {
            dispatch(ConsultantActionCreators.requestConsultantsPeppolStatus(instance, teamsState, consultantQuickView));
        }
    }, [consultants]);

    //const consultantQuickView = consultants && consultants.length === 1
    //    ? consultants[0]
    //    : (consultants && UserLoginState.rigths && UserLoginState.rigths.consultantid
    //        ?
    //        consultants.find(c => UserLoginState.rigths && UserLoginState.rigths.consultantid !== undefined && c.consultantid === UserLoginState.rigths.consultantid)
    //        :
    //        null
    //    );
    if (loggedConsultant === null || loggedConsultant === undefined) {
        return null;
    }
    return <div className={stylesGrid.FlexHomeNonAdmin}>
        <div className={styles.ContentBoxBrandBackground}>
            <div className={styles.ContentBoxBrandBackgroundGrid} >
                {
                    loggedConsultant.photo && <Avatar
                        className={stylesTab.AfterAvatarMargin}
                        size={96}
                        image={{
                            alt: loggedConsultant.displayName ? loggedConsultant.displayName : "?",
                            src: loggedConsultant.photo,
                        }}
                    />
                }
                <Title3>{(loggedConsultant.displayName ? loggedConsultant.displayName : "?")}</Title3>
            </div>
        </div>
        <div className={styles.ContentBoxBrandBackground}>
            <div className={styles.ContentBoxBrandBackgroundGrid} >
                <Caption1>{t('FirstName')}</Caption1><Body1Stronger>{loggedConsultant.companyName ? loggedConsultant.firstName : t('NotDefined')}</Body1Stronger>
                <Caption1>{t('LastName')}</Caption1><Body1Stronger>{loggedConsultant.companyName ? loggedConsultant.lastName : t('NotDefined')}</Body1Stronger>
            </div>
        </div>

        <div className={styles.ContentBoxBrandBackground}>
            <div className={styles.ContentBoxBrandBackgroundGrid} >
                <Caption1>{t('CompanyName')}</Caption1><Body1Stronger>{loggedConsultant.companyName ? loggedConsultant.companyName : t('NotDefined')}</Body1Stronger>
                <Caption1>{t('PrivateEmail')}</Caption1><Body1Stronger>{loggedConsultant.privateEmail ? loggedConsultant.privateEmail : t('NotDefined')}</Body1Stronger>
                <Caption1>{t('PhoneNumber')}</Caption1><Body1Stronger>{loggedConsultant.phoneNumber ? loggedConsultant.phoneNumber : t('NotDefined')}</Body1Stronger>
                <Caption1>{t('website')}</Caption1><Body1Stronger>{loggedConsultant.website ? loggedConsultant.website : t('NotDefined')}</Body1Stronger>
                <Caption1>{t('CompanyNumber')}</Caption1><Body1Stronger>{loggedConsultant.companyNumber ? loggedConsultant.companyNumber : t('NotDefined')}</Body1Stronger>
                <Caption1>{t('VAT')}</Caption1><Body1Stronger>{loggedConsultant.vat ? loggedConsultant.vat : t('NotDefined')}</Body1Stronger>
                <Caption1>Iban</Caption1><Body1Stronger>{(loggedConsultant.iban ? loggedConsultant.iban : t('NotDefined')) + (loggedConsultant.bic ? (" (" + loggedConsultant.bic + ")") : "")}</Body1Stronger>
                <Caption1>{t('Address')}</Caption1>
                <div style={{ display: "flex", flexDirection: "column" }}>
                    {
                        loggedConsultant.address === null
                        && loggedConsultant.address2 === null
                        && loggedConsultant.address3 === null
                        && loggedConsultant.postalCode === null
                        && loggedConsultant.city === null
                        && loggedConsultant.country === null
                        && <Body1Stronger>{t('NotDefined')}</Body1Stronger>
                    }
                    {loggedConsultant.address && <Body1Stronger>{loggedConsultant.address}</Body1Stronger>}
                    {loggedConsultant.address2 && <Body1Stronger>{loggedConsultant.address}</Body1Stronger>}
                    {loggedConsultant.address3 && <Body1Stronger>{loggedConsultant.address}</Body1Stronger>}
                    {loggedConsultant.postalCode && <Body1Stronger>{loggedConsultant.postalCode}</Body1Stronger>}
                    {loggedConsultant.city && <Body1Stronger>{loggedConsultant.city}</Body1Stronger>}
                    {loggedConsultant.country && <Body1Stronger>{loggedConsultant.country}</Body1Stronger>}
                </div>
            </div>
        </div>
        <div className={styles.ContentBoxBrandBackground}>
            <div className={styles.ContentBoxBrandBackgroundGrid} >
                <Caption1>Peppol</Caption1>
                <div className={styles.ContentBoxBrandBackgroundGrid} >
                    {loggedConsultant.peppolStatus === undefined &&
                        <Spinner size="tiny" />
                    }
                    {loggedConsultant.peppolStatus && loggedConsultant.peppolStatus.prod.length === 0 &&
                        <Body1Stronger>{t('NotRegistered')}</Body1Stronger>
                    }
                    {loggedConsultant.peppolStatus &&
                        loggedConsultant.peppolStatus.prod.map(e => {
                            return <>
                                <Button appearance="transparent"
                                    style={{ padding: 0, justifyContent: "start" }}
                                    onClick={() => GotoDirectoryPeppolWeb(e.peppolIdentifier)}>
                                    <Body1Stronger>{e.peppolIdentifier}</Body1Stronger>
                                </Button>
                                <div style={{ display: "flex", flexDirection: "column", gap: "4px", alignItems: "flex-start", width: "max-content" }}>
                                    <Body1Stronger>{(e.isRegistered ? t('Registered') : t('NotRegistered'))}</Body1Stronger>
                                    <Badge appearance="filled" color={e.supportedDocuments && e.supportedDocuments.canReceiveInvoices ? "success" : "danger"}>
                                        Invoice
                                    </Badge>
                                    <Badge appearance="filled" color={e.supportedDocuments && e.supportedDocuments.canReceiveCreditNotes ? "success" : "danger"}>
                                        Credit Note
                                    </Badge>
                                    <Badge appearance="filled" color={e.supportedDocuments && e.supportedDocuments.canReceiveMLR ? "success" : "informative"}>
                                        MLR
                                    </Badge>
                                    <Badge appearance="filled" color={e.supportedDocuments && e.supportedDocuments.canReceiveInvoiceResponse ? "success" : "informative"}>
                                        Invoice Response
                                    </Badge>
                                </div>
                            </>;
                        })
                    }
                </div>
                {UserLoginState && UserLoginState.rigths && UserLoginState.rigths.isAdmin && <>
                    <Caption1>Peppol Test Env</Caption1>
                    <div className={styles.ContentBoxBrandBackgroundGrid} >
                        {loggedConsultant.peppolStatus === undefined &&
                            <Spinner size="tiny" />
                        }
                        {loggedConsultant.peppolStatus &&
                            loggedConsultant.peppolStatus.test.map(e => {
                                return <>
                                    <Button appearance="transparent"
                                        style={{ padding: 0, justifyContent: "start" }}
                                        onClick={() => GotoDirectoryPeppolWeb(e.peppolIdentifier)}>
                                        <Body1Stronger>{e.peppolIdentifier}</Body1Stronger>
                                    </Button>
                                    <div style={{ display: "flex", flexDirection: "column", gap: "4px", alignItems: "flex-start", width: "max-content" }}>
                                        <Body1Stronger>{(e.isRegistered ? t('Registered') : t('NotRegistered'))}</Body1Stronger>
                                        <Badge appearance="filled" color={e.supportedDocuments && e.supportedDocuments.canReceiveInvoices ? "success" : "warning"}>
                                            Invoice
                                        </Badge>
                                        <Badge appearance="filled" color={e.supportedDocuments && e.supportedDocuments.canReceiveCreditNotes ? "success" : "warning"}>
                                            Credit Note
                                        </Badge>
                                        <Badge appearance="filled" color={e.supportedDocuments && e.supportedDocuments.canReceiveMLR ? "success" : "informative"}>
                                            MLR
                                        </Badge>
                                        <Badge appearance="filled" color={e.supportedDocuments && e.supportedDocuments.canReceiveInvoiceResponse ? "success" : "informative"}>
                                            Invoice Response
                                        </Badge>
                                    </div>
                                </>;
                            })
                        }
                    </div>
                </>
                }
            </div>
        </div>
    </div>;
};

const GetYearFromStorage = (): number => {
    const dy = new Date().getFullYear();
    const y = localStorage.getItem("DashBoardYear");
    if (y !== null) {
        const yy = parseInt(y, 10);
        if (yy >= 2022 && yy <= dy) {
            return yy;
        }
    }
    return dy;
};
const SetYearToStorage = (y: number) => {
    localStorage.setItem("DashBoardYear", y.toString());
};

export const HomeAdmin = () => {
    const data = useSelector<ApplicationState, DataState>((state: ApplicationState) => state.data);
    const { invoices, invoicesLoading } = useInvoices();
    const { timeSheetEntries, timeSheetEntriesLoading } = useTimeSheetEntries();
    const { sales, salesLoading } = useSales();
    const { projectsAssigments, projectsAssigmentsLoading } = useProjectsAssigments();
    const { articles, articlesLoading } = useArticles();
    const { products, productsLoading } = useProducts();
    const { customers } = useCustomers();
    const { consultants, consultantsLoading } = useConsultants();
    const { fixePriceConsultants, fixePriceConsultantsLoading } = useFixePriceConsultants();
    const consultantsPhotoLoaded = useSelector((state: ApplicationState) => state.consultantsState.consultantsPhotoLoaded);
    const [year, setYear] = React.useState<number>(GetYearFromStorage());
    const { instance } = useMsal();
    const dispatch = useDispatch();
    const teamsState = useSelector<ApplicationState, TeamsState>((state: ApplicationState) => state.teamsState);
    const [countElement, setCountElement] = React.useState<CountElement[]>([]);
    const [productElement, setProductElement] = React.useState<ProductResume[]>([]);
    const [consultantCountElement, setConsultantCountElement] = React.useState<ConsultantResume[]>([]);
    const { t } = useTranslation();
    const UserLoginState = useSelector<ApplicationState, UserLoginState>((state: ApplicationState) => state.userNeomyticLogin);
    const [totalDu, setTotalDu] = React.useState<number | null>(null);
    const [totalDuTTC, setTotalDuTTC] = React.useState<number | null>(null);
    const totalRevenu = React.useMemo(() => countElement.reduce(function (accumulator, e) { return accumulator + e.revenue + e.salesRevenue + e.otherRevenue; }, 0), [countElement]);
    const totalCost = React.useMemo(() => countElement.reduce(function (accumulator, e) { return accumulator - e.cost; }, 0), [countElement]);
    const totalProductRevenu = React.useMemo(() => productElement.reduce(function (accumulator, e) { return accumulator + e.revenue; }, 0), [productElement]);
    const bigWidth = useMediaQuery('(min-width: 768px)');
    const stylesTab = useTabStyles();
    const stylesEditor = useEditorStyles();
    const stylesGrid = useGridStyles();

    React.useEffect(() => {
        if (invoices !== undefined
            && timeSheetEntries != undefined
            && sales != undefined
            && projectsAssigments != undefined
            && articles != undefined
            && customers != undefined) {
            let total = 0;
            let totalTTC = 0;

            invoices.filter((i => IsNeomyticConsultant(i.consultantid))).forEach((i) => {
                if (i.payment === 0) {
                    total = total + DataHelpers.GetInvoiceTotalFromInvoiceHTVA(data, i);
                    const cust = customers ? customers.find(e => e.customerid === i.customerid) : undefined;
                    const cons = consultants ? consultants.find(e => e.consultantid === i.consultantid) : undefined;
                    totalTTC = totalTTC + DataHelpers.GetInvoiceTotalFromInvoice(data, cons, cust, i);
                }
            });
            setTotalDu(total);
            setTotalDuTTC(totalTTC);
        }
    }, [invoices, timeSheetEntries, sales, projectsAssigments, articles, customers]);

    React.useEffect(() => {
        if (consultants && !consultantsLoading && consultantsPhotoLoaded === false) {
            TokenHelper.GetGraphToken(instance, teamsState, UserLoginState.graphToken)
                .then(token => {
                    if (token) dispatch(ConsultantActionCreators.requestConsultantsPhoto(token));
                });
        }
    }, [instance, teamsState, UserLoginState, consultants, consultantsLoading, consultantsPhotoLoaded]);


    React.useEffect(() => {
        if (timeSheetEntries !== undefined && sales !== undefined && invoices !== undefined
            && products !== undefined && articles !== undefined && projectsAssigments !== undefined
            && consultants !== undefined && fixePriceConsultants !== undefined
        ) {
            let ConsultantCountElements: ConsultantResume[] = consultants
                ? consultants
                    //.sort(SortHelper.ConsultantByName)
                    .map((c): ConsultantResume => {
                        return { consultant: c, e: [], revenue: 0, cost: 0 };
                    })
                : [];
            ConsultantCountElements.forEach((ce) => {
                ce.e = [...Array(12)].map((_, index): CountElement => {
                    const cd = new Date(Date.UTC(year, index, 1));
                    const isActif = DataHelpers.IsConsultantActifOn(ce.consultant, cd);
                    const a = DataHelpers.GetConsultantFixePriceForMounth(fixePriceConsultants, ce.consultant, new Date(Date.UTC(year, index)));
                    ce.cost = ce.cost - a;
                    return { key: index + + (year * 100), month: index, year: year, revenue: 0, salesRevenue: 0, otherRevenue: 0, cost: -a, isActif: isActif };
                });
            });
            const CountElements: CountElement[] = [...Array(12)].map((_, index): CountElement => {
                const e: CountElement = { key: index + + (year * 100), month: index, year: year, revenue: 0, salesRevenue: 0, otherRevenue: 0, cost: 0, isActif: true };
                ConsultantCountElements.forEach((ce) => {
                    e.cost = e.cost + ce.e[index].cost;
                });
                return e;
            });
            timeSheetEntries.forEach((t) => {
                const pa = projectsAssigments ? projectsAssigments.find(e => e.projectAssignmentid === t.projectAssignmentid) : undefined;
                const co = pa && consultants ? consultants.find(e => e.consultantid === pa.consultantid) : undefined;

                if (t.date) {
                    const tdate = new Date(t.date);
                    if (tdate.getFullYear() === year) {
                        const customerPrice = pa ? pa.customerPrice * t.count : 0;
                        const consultantPrice = pa ? (pa.consultantPrice * t.count) : 0;

                        const tdate = new Date(t.date);
                        const key = tdate.getMonth() + (tdate.getFullYear() * 100);
                        // handle Global revenues
                        const e = CountElements.findIndex(x => x.key === key);
                        if (e !== -1) {
                            CountElements[e].revenue = CountElements[e].revenue + customerPrice;
                            CountElements[e].cost = CountElements[e].cost - consultantPrice;
                        }
                        // handle consultant revenues
                        if (co) {
                            const cce = ConsultantCountElements.find(x => x.consultant.consultantid === co.consultantid);
                            if (cce) {
                                const e = cce.e.findIndex(x => x.key === key);
                                if (e !== -1) {
                                    cce.e[e].revenue = cce.e[e].revenue + customerPrice;
                                    cce.e[e].cost = cce.e[e].cost - consultantPrice;
                                }
                                cce.revenue = cce.revenue + customerPrice;
                                cce.cost = cce.cost - consultantPrice;
                            }
                        }
                    }
                }
            });

            const ProductsRes: ProductResume[] = products
                ? products.sort(SortHelper.productByName).map((p): ProductResume => {
                    return { product: p, e: [], revenue: 0 };
                })
                : [];

            ProductsRes.forEach((ce) => {
                ce.e = [...Array(12)].map((_, index): CountElement => {
                    return { key: index + + (year * 100), month: index, year: year, revenue: 0, salesRevenue: 0, otherRevenue: 0, cost: 0, isActif: true };
                });
            });

            sales.forEach((s) => {
                if (s.date) {
                    const tdate = new Date(s.date);
                    if (tdate.getFullYear() === year) {
                        const key: number = tdate.getMonth() + (tdate.getFullYear() * 100);
                        const price: number = s.unitPrice * s.count;
                        // handle Global revenues
                        const e = CountElements.findIndex(x => x.key === key);
                        if (e !== -1) {
                            CountElements[e].salesRevenue = CountElements[e].salesRevenue + price;
                        }
                        // handle product revenues
                        const a = articles ? articles.find(e => e.articleid === s.articleid) : undefined;
                        if (a) {
                            const p = ProductsRes.find(p => p.product.productid === a.productid);
                            if (p) {
                                const e2 = CountElements.findIndex(x => x.key === key);
                                if (e2 !== -1) {
                                    p.e[e].revenue = p.e[e].revenue + price;
                                }
                                p.revenue = p.revenue + price;
                            }
                        }
                    }
                }
            });

            //InvoiceEntries.forEach((en) => {
            //    const i = invoices ? invoices.find(ii => ii.invoiceid == en.invoiceid) : undefined;
            //    if (i && i.invoiceDate) {
            //        const tdate = new Date(i.invoiceDate);
            //        if (tdate.getFullYear() === year) {
            //            const key = tdate.getMonth() + (tdate.getFullYear() * 100);
            //            // handle Global revenues
            //            const e = CountElements.findIndex(x => x.key === key);
            //            if (e !== -1) {
            //                CountElements[e].otherRevenue = CountElements[e].otherRevenue + (en.sellPrice * en.count);
            //                CountElements[e].cost = CountElements[e].cost - (en.costPrice * en.count);
            //            }
            //        }
            //    }
            //});

            invoices.filter(i => IsNeomyticConsultant(i.consultantid)).forEach((i) => {
                if (i.invoiceDate) {
                    const tdate = new Date(i.invoiceDate);
                    if (tdate.getFullYear() === year) {
                        const key = tdate.getMonth() + (tdate.getFullYear() * 100);
                        // handle Global revenues
                        const e = CountElements.findIndex(x => x.key === key);
                        if (e !== -1 && i.invoiceEntries) {
                            i.invoiceEntries.forEach((ie) => {
                                CountElements[e].otherRevenue = CountElements[e].otherRevenue + (ie.sellPrice * ie.count);
                                CountElements[e].cost = CountElements[e].cost - (ie.costPrice * ie.count);
                            });
                        }
                    }
                }
            });


            // filter out consultants that does not have any activities
            ConsultantCountElements = ConsultantCountElements.filter(cp =>
                cp.e.find(cpe => cpe.isActif) !== undefined
            );

            setProductElement(ProductsRes);
            setCountElement(CountElements);
            setConsultantCountElement(ConsultantCountElements);
        }
    }, [timeSheetEntries, sales, invoices, year, products, articles, projectsAssigments, consultants, fixePriceConsultants]);

    const options = {
        responsive: true,
        plugins: {
            legend: {
                display: false,
                position: 'right' as const,
            },
            title: {
                display: true,
                text: t('Earnings') + '/' + t('Costs'),
            },
        },
        scales: {
            x: {
                stacked: true,
            },
            y: {
                stacked: true
            }
        }
    };

    const labels = countElement.map(e => ((e.month + 1) + "/" + e.year));

    const graphData = {
        labels: labels,
        datasets: [
            {
                label: t('Earnings'),
                data: countElement.map(e => (e.revenue + e.salesRevenue + e.otherRevenue)),
                borderColor: 'rgb(54, 162, 235)',
                backgroundColor: 'rgba(54, 162, 235, 0.5)',
                stack: 'Stack 0',
            },
            {
                label: t('Costs'),
                data: countElement.map(e => e.cost),
                borderColor: 'rgb(255, 99, 132)',
                backgroundColor: 'rgba(255, 99, 132, 0.5)',
                stack: 'Stack 0',
            },
            {
                label: t('MarginConsultants'),
                data: countElement.map(e => e.revenue + e.cost),
                borderColor: 'rgb(255, 159, 64)',
                backgroundColor: 'rgba(255, 159, 64, 0.2)',
                stack: 'Stack 1',
            },
            {
                label: t('RevenueProducts'),
                data: countElement.map(e => e.salesRevenue),
                borderColor: 'rgb(214, 201, 24)',
                backgroundColor: 'rgba(214, 201, 24, 0.6)',
                stack: 'Stack 1',
            },
        ]
    };

    const dd = new Date(Date.now());
    const MinAnualMargin = dd.getMonth() * 600;

    return <div className={stylesTab.rootSub}>
        <div className={stylesTab.MainBar}>
            <h2>Status</h2>
            <div className={mergeClasses(stylesTab.MarginLeftAuto, stylesEditor.Horizontal)}>
                {t('FiscalYear')}
                <Dropdown
                    positioning="below"
                    listbox={{
                        className: stylesEditor.optionContainer,
                    }}
                    className={stylesEditor.fluid}
                    onOptionSelect={(event, data) => {
                        if (data.optionValue !== undefined) {
                            switch (data.optionValue) {
                                case "2021": {
                                    setYear(2021);
                                    SetYearToStorage(2021);
                                    break;
                                }
                                case "2022": {
                                    setYear(2022);
                                    SetYearToStorage(2022);
                                    break;
                                }
                                case "2023": {
                                    setYear(2023);
                                    SetYearToStorage(2023);
                                    break;
                                }
                                case "2024": {
                                    setYear(2024);
                                    SetYearToStorage(2024);
                                    break;
                                }
                                case "2025": {
                                    setYear(2025);
                                    SetYearToStorage(2025);
                                    break;
                                }
                                case "2026": {
                                    setYear(2026);
                                    SetYearToStorage(2026);
                                    break;
                                }
                            }
                        }
                    }}
                    value={year.toString()}
                    selectedOptions={[year.toString()]}
                >
                    <Option key="2022" value="2022">2022</Option>
                    <Option key="2023" value="2023">2023</Option>
                    <Option key="2024" value="2024">2024</Option>
                    <Option key="2025" value="2025">2025</Option>
                    <Option key="2026" value="2026">2026</Option>
                </Dropdown>
            </div>
        </div>
        {totalDu && totalDuTTC
            ?
            <>{t('DueByCustomers')} {totalDu.toLocaleString(undefined, { style: 'currency', currency: 'EUR' })} ({totalDuTTC.toLocaleString(undefined, { style: 'currency', currency: 'EUR' })} {t('IncludVAT')})</>
            :
            <Skeleton animation="pulse">
                <SkeletonItem className={stylesTab.width55} />
            </Skeleton>
        }

        <Divider />
        <h2>{t('PeriodeView')}</h2>
        {bigWidth
            ?
            <div className={stylesTab.flexrow100}>
                <div className={stylesTab.flexcolumn100}>
                    <div className={stylesGrid.GridPeriodeView}>
                        <Text key="tsg1Menu00">{" "}</Text>
                        <Text key="tsg1Menu01" className={stylesTab.centerInfo}>{t('Earnings')}</Text>
                        <Text key="tsg1Menu02" className={stylesTab.centerInfo}>{t('Costs')}</Text>
                        <Text key="tsg1Menu03" className={stylesTab.centerInfo}>{t('MarginConsultants')}</Text>
                        <Text key="tsg1Menu04" className={stylesTab.centerInfo}>{t('RevenueProducts')}</Text>
                    </div>
                    {countElement.length > 0
                        ?
                        countElement.map((ge, index) =>
                            <div key={"ts1Ske0" + index} className={stylesGrid.GridPeriodeView}>
                                <Text key={"ts1Ske1" + index}>{(ge.month + 1) + "/" + ge.year}</Text>
                                <Text key={"ts1Ske2" + index} className={stylesTab.centerInfo}>{(ge.salesRevenue + ge.revenue + ge.otherRevenue).toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                                <Text key={"ts1Ske3" + index} className={stylesTab.centerInfo}>{(ge.cost === 0 ? 0 : -ge.cost).toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                                {(ge.revenue + ge.cost > 0)
                                    ? ((ge.revenue + ge.cost > 600)
                                        ? <Text className={stylesTab.InlineCenteredSuccessText}>{(ge.revenue + ge.cost).toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                                        : <Text className={stylesTab.InlineCenteredWarningText}>{(ge.revenue + ge.cost).toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>)
                                    : <Text className={stylesTab.InlineCenteredDangerText}>{(ge.revenue + ge.cost).toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                                }
                                {(ge.salesRevenue > 0)
                                    ? ((ge.salesRevenue > 600)
                                        ? <Text className={stylesTab.InlineCenteredSuccessText}>{(ge.salesRevenue).toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                                        : <Text className={stylesTab.InlineCenteredWarningText}>{(ge.salesRevenue).toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>)
                                    : <Text className={stylesTab.InlineCenteredDangerText}>{(ge.salesRevenue).toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                                }
                            </div>
                        )
                        :
                        [...Array(12)].map((value: any, index: number) =>
                            <div key={"wait" + index} className={stylesGrid.GridPeriodeView}>
                                <div className={mergeClasses(stylesTab.width100CenterContainer, stylesTab.marginBottom6px)}>
                                    <Skeleton animation="pulse" className={stylesTab.width100}>
                                        <SkeletonItem className={stylesTab.width35} />
                                    </Skeleton>
                                </div>
                                <div className={stylesTab.width100CenterContainer}>
                                    <Skeleton animation="pulse" className={stylesTab.width55}>
                                        <SkeletonItem className={stylesTab.width100} />
                                    </Skeleton>
                                </div>
                                <div className={stylesTab.width100CenterContainer}>
                                    <Skeleton animation="pulse" className={stylesTab.width55}>
                                        <SkeletonItem className={stylesTab.width100} />
                                    </Skeleton>
                                </div>
                                <div className={stylesTab.width100CenterContainer}>
                                    <Skeleton animation="pulse" className={stylesTab.width55}>
                                        <SkeletonItem className={stylesTab.width100} />
                                    </Skeleton>
                                </div>
                                <div className={stylesTab.width100CenterContainer}>
                                    <Skeleton animation="pulse" className={stylesTab.width55}>
                                        <SkeletonItem className={stylesTab.width100} />
                                    </Skeleton>
                                </div>
                            </div>
                        )
                    }
                    <Divider className={stylesTab.UnsetFlexGrow} />
                    {countElement.length > 0
                        ?
                        <div className={stylesGrid.GridPeriodeView}>
                            <Text>Total</Text>
                            <Text className={stylesTab.centerInfo}>{totalRevenu.toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                            <Text className={stylesTab.centerInfo}>{totalCost.toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                            {(totalRevenu - totalProductRevenu - totalCost > 0)
                                ? ((totalRevenu - totalProductRevenu - totalCost > 600)
                                    ? <Text className={stylesTab.InlineCenteredSuccessText}>{(totalRevenu - totalProductRevenu - totalCost).toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                                    : <Text className={stylesTab.InlineCenteredWarningText}>{(totalRevenu - totalProductRevenu - totalCost).toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>)
                                : <Text className={stylesTab.InlineCenteredDangerText}>{(totalRevenu - totalProductRevenu - totalCost).toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                            }
                            {(totalProductRevenu > 0)
                                ? <Text className={stylesTab.InlineCenteredSuccessText}>{(totalProductRevenu).toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                                : <Text className={stylesTab.InlineCenteredDangerText}>{(totalProductRevenu).toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                            }
                        </div>
                        :
                        <div className={stylesGrid.GridPeriodeView}>
                            <Text content="Total" />
                            <div className={stylesTab.width100CenterContainer}>
                                <Skeleton animation="pulse" className={stylesTab.width55}>
                                    <SkeletonItem className={stylesTab.width100} />
                                </Skeleton>
                            </div>
                            <div className={stylesTab.width100CenterContainer}>
                                <Skeleton animation="pulse" className={stylesTab.width55}>
                                    <SkeletonItem className={stylesTab.width100} />
                                </Skeleton>
                            </div>
                            <div className={stylesTab.width100CenterContainer}>
                                <Skeleton animation="pulse" className={stylesTab.width55}>
                                    <SkeletonItem className={stylesTab.width100} />
                                </Skeleton>
                            </div>
                            <div className={stylesTab.width100CenterContainer}>
                                <Skeleton animation="pulse" className={stylesTab.width55}>
                                    <SkeletonItem className={stylesTab.width100} />
                                </Skeleton>
                            </div>
                        </div>
                    }
                </div>
                <div className={mergeClasses(stylesTab.flexcolumn100, stylesTab.overflowauto)}>
                    <Bar options={options} data={graphData} />
                </div>
            </div>
            :
            <div className={stylesTab.flexcolumn100}>
                <div className={stylesGrid.GridPeriodeViewSmall}>
                    <Text>{" "}</Text>
                    <Text className={stylesTab.centerInfo}>{t('Earnings')}</Text>
                    <Text className={stylesTab.centerInfo}>{t('Costs')}</Text>
                    <Text className={stylesTab.centerInfo}>{t('Balance')}</Text>
                </div>
                {countElement.length > 0
                    ?
                    countElement.map((ge) => {
                        return (
                            <div className={stylesGrid.GridPeriodeViewSmall}>
                                <Text> {(ge.month + 1) + "/" + ge.year}</Text>
                                <Text className={stylesTab.centerInfo}> {(ge.salesRevenue + ge.revenue).toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                                <Text className={stylesTab.centerInfo}> {(ge.cost === 0 ? 0 : -ge.cost).toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                                {(ge.revenue + ge.cost > 0)
                                    ? ((ge.revenue + ge.salesRevenue + ge.cost > 600)
                                        ? <Text className={stylesTab.InlineCenteredSuccessText}>{(ge.revenue + ge.salesRevenue + ge.cost).toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                                        : <Text className={stylesTab.InlineCenteredWarningText}>{(ge.revenue + ge.salesRevenue + ge.cost).toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>)
                                    : <Text className={stylesTab.InlineCenteredDangerText}>{(ge.revenue + ge.salesRevenue + ge.cost).toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                                }
                            </div>);
                    })
                    :
                    [...Array(12)].map((_, index) => {
                        return (
                            <div className={stylesGrid.GridPeriodeViewSmall}>
                                <div className={stylesTab.width100CenterContainer}>
                                    <Text>{(index + 1) + "/"}</Text>
                                    <Skeleton animation="pulse" className={stylesTab.width100}>
                                        <SkeletonItem className={stylesTab.width35} />
                                    </Skeleton>
                                </div>
                                <div className={stylesTab.width100CenterContainer}>
                                    <Skeleton animation="pulse" className={stylesTab.width55}>
                                        <SkeletonItem className={stylesTab.width100} />
                                    </Skeleton>
                                </div>
                                <div className={stylesTab.width100CenterContainer}>
                                    <Skeleton animation="pulse" className={stylesTab.width55}>
                                        <SkeletonItem className={stylesTab.width100} />
                                    </Skeleton>
                                </div>
                                <div className={stylesTab.width100CenterContainer}>
                                    <Skeleton animation="pulse" className={stylesTab.width55}>
                                        <SkeletonItem className={stylesTab.width100} />
                                    </Skeleton>
                                </div>
                            </div>);
                    })}
                <Divider />
                {countElement.length > 0
                    ?
                    <div className={stylesGrid.GridPeriodeViewSmall}>
                        <Text>Total</Text>
                        <Text className={stylesTab.centerInfo}>{totalRevenu.toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                        <Text className={stylesTab.centerInfo}>{totalCost.toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                        {(totalRevenu - totalCost > 0)
                            ? ((totalRevenu - totalCost > 600)
                                ? <Text className={stylesTab.InlineCenteredSuccessText}>{(totalRevenu - totalCost).toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                                : <Text className={stylesTab.InlineCenteredWarningText}>{(totalRevenu - totalCost).toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>)
                            : <Text className={stylesTab.InlineCenteredDangerText}>{(totalRevenu - totalCost).toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                        }
                    </div>
                    :
                    <div className={stylesGrid.GridPeriodeViewSmall}>
                        <Text>Total</Text>
                        <div className={stylesTab.width100CenterContainer}>
                            <Skeleton animation="pulse">
                                <SkeletonItem className={stylesTab.width35} />
                            </Skeleton>
                        </div>
                        <div className={stylesTab.width100CenterContainer}>
                            <Skeleton animation="pulse">
                                <SkeletonItem className={stylesTab.width35} />
                            </Skeleton>
                        </div>
                        <div className={stylesTab.width100CenterContainer}>
                            <Skeleton animation="pulse">
                                <SkeletonItem className={stylesTab.width35} />
                            </Skeleton>
                        </div>
                    </div>
                }
            </div>
        }
        <Divider />
        <h2>{t('PeriodeViewOfConsultants')}</h2>
        {bigWidth ?
            <div className={stylesGrid.GridPeriodeViewOfConsultants}>
                <Text key="pvcM">{" "}</Text>
                {[...Array(12)].map((_, index) => <Text key={"pvcM" + index} className={stylesTab.centerInfo}>{(index + 1) + "/" + year}</Text>)}
                <Text key={"pvcMT"} className={stylesTab.centerInfo}>{" Total " + year}</Text>
                {
                    consultantCountElement.map((c) => <>
                        <div className={stylesTab.VAlign}>
                            {c.consultant.photo && <Avatar
                                className={stylesTab.AfterAvatarMargin}
                                size={56}
                                image={{
                                    alt: c.consultant.displayName ? c.consultant.displayName : "?",
                                    src: c.consultant.photo,
                                }}
                            />}
                            <Button appearance="transparent" className={stylesTab.TestButton}
                                onClick={() => dispatch(ConsultantActionCreators.requestConslutantQuickView(c.consultant))}>
                                {c.consultant.displayName}
                            </Button>
                        </div>
                        {
                            c.e.map((ce) =>
                                ce.isActif
                                    ?
                                    <div className={stylesTab.columnpaddind10}>
                                        <Text className={stylesTab.centerInfo}>{ce.revenue.toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                                        <Text className={stylesTab.centerInfo}>{(ce.cost === 0 ? 0 : ce.cost).toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                                        {(ce.revenue + ce.cost > 0)
                                            ? ((ce.revenue + ce.cost > 600)
                                                ? <Text className={stylesTab.InlineCenteredSuccessText}>{(ce.revenue + ce.cost).toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                                                : <Text className={stylesTab.InlineCenteredWarningText}>{(ce.revenue + ce.cost).toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>)
                                            : <Text className={stylesTab.InlineCenteredDangerText}>{(ce.revenue + ce.cost).toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                                        }
                                    </div>
                                    :
                                    <div className={stylesTab.columnpaddind10}>
                                        <Text className={stylesTab.centerInfo}>-</Text>
                                        <Text className={stylesTab.centerInfo}>-</Text>
                                        <Text className={stylesTab.centerInfo}>-</Text>
                                    </div>
                            )
                        }
                        <div className={stylesTab.columnpaddind10}>
                            <Text className={stylesTab.centerInfo}>{c.revenue.toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                            <Text className={stylesTab.centerInfo}>{c.cost.toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                            {(c.revenue + c.cost > 0)
                                ? ((c.revenue + c.cost > MinAnualMargin)
                                    ? <Text className={stylesTab.InlineCenteredSuccessText}>{(c.revenue + c.cost).toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                                    : <Text className={stylesTab.InlineCenteredWarningText}>{(c.revenue + c.cost).toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>)
                                : <Text className={stylesTab.InlineCenteredDangerText}>{(c.revenue + c.cost).toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                            }
                        </div>
                    </>)
                }
            </div>
            : <div className={stylesGrid.GridPeriodeViewOfConsultantsSmall}>
                <Text>{" "}</Text>
                <Text className={stylesTab.centerInfo}>{" Total " + year}</Text>
                {
                    consultantCountElement.map((c) => <>
                        <div className={stylesTab.VAlign}>
                            {c.consultant.photo && <Avatar
                                className={stylesTab.AfterAvatarMargin}
                                size={36}
                                image={{
                                    alt: c.consultant.displayName ? c.consultant.displayName : "?",
                                    src: c.consultant.photo,
                                }}
                            />}
                            <Button appearance="transparent" className={stylesTab.TestButton}
                                onClick={() => dispatch(ConsultantActionCreators.requestConslutantQuickView(c.consultant))}>
                                {c.consultant.displayName}
                            </Button>
                        </div>
                        <div className={stylesTab.columnpaddind10}>
                            <Text className={stylesTab.centerInfo}>{c.revenue.toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                            <Text className={stylesTab.centerInfo}>{c.cost.toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                            {(c.revenue + c.cost > 0)
                                ? ((c.revenue + c.cost > MinAnualMargin)
                                    ? <Text className={stylesTab.InlineCenteredSuccessText}>{(c.revenue + c.cost).toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                                    : <Text className={stylesTab.InlineCenteredWarningText}>{(c.revenue + c.cost).toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>)
                                : <Text className={stylesTab.InlineCenteredDangerText}>{(c.revenue + c.cost).toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                            }
                        </div>
                    </>)
                }
            </div>
        }
        <Divider />
        <h2>Products</h2>
        {bigWidth ?
            <div className={stylesGrid.GridPeriodeViewOfConsultants}>
                <Text key="prvM" >{" "}</Text>
                {[...Array(12)].map((_, index) => <Text key={"prvM" + index} className={stylesTab.centerInfo} >{(index + 1) + "/" + year}</Text>)}
                <Text key="prvMT" className={stylesTab.centerInfo}>{" Total " + year}</Text>
                {
                    productElement.filter(p => p.revenue !== 0).map((p) => <>
                        <div className={stylesTab.VAlign}>
                            <Button appearance="transparent" className={stylesTab.TestButton}
                                onClick={() => dispatch(dataActionCreators.requestProductQuickView(p.product))}>
                                {p.product.displayName}
                            </Button>
                        </div>
                        {
                            p.e.map((ce) => <div className={stylesTab.columnpaddind10}>
                                {(ce.revenue > 0)
                                    ? <Text className={stylesTab.InlineCenteredSuccessText}>{(ce.revenue).toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                                    : <Text className={stylesTab.InlineCenteredDangerText}>{(ce.revenue).toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                                }
                            </div>)
                        }
                        <div className={stylesTab.columnpaddind10}>
                            {(p.revenue > 0)
                                ? <Text className={stylesTab.InlineCenteredSuccessText}>{(p.revenue).toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                                : <Text className={stylesTab.InlineCenteredDangerText}>{(p.revenue).toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                            }
                        </div>
                    </>)
                }
                {[...Array(13)].map((_, index) => <Text key={"prvTML" + index} className={stylesTab.centerInfo} >{""}</Text>
                )}
                <Divider />
                {[...Array(13)].map((_, index) => <Text key={"prvTM" + index} className={stylesTab.centerInfo} >{""}</Text>)}
                <div className={stylesTab.columnpaddind10}>
                    {(totalProductRevenu > 0)
                        ? <Text className={stylesTab.InlineCenteredSuccessText}>{(totalProductRevenu).toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                        : <Text className={stylesTab.InlineCenteredDangerText}>{(totalProductRevenu).toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                    }
                </div>
            </div>
            :
            <div className={stylesGrid.GridPeriodeViewOfConsultantsSmall}>
                <Text>{" "}</Text>
                <Text className={stylesTab.centerInfo}>{" Total " + year}</Text>
                {
                    productElement.map((p) => <>
                        <div className={stylesTab.VAlign}>
                            <Button appearance="transparent" className={stylesTab.TestButton}
                                onClick={() => dispatch(dataActionCreators.requestProductQuickView(p.product))}>
                                {p.product.displayName}
                            </Button>
                        </div>
                        <div className={stylesTab.columnpaddind10}>
                            {(p.revenue > 0)
                                ? <Text className={stylesTab.InlineCenteredSuccessText}>{(p.revenue).toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                                : <Text className={stylesTab.InlineCenteredDangerText}>{(p.revenue).toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                            }
                        </div>
                    </>)
                }
                <Text>{" "}</Text>
                <Divider />
                <Text>{" "}</Text>
                <div className={stylesTab.columnpaddind10}>
                    {(totalProductRevenu > 0)
                        ? <Text className={stylesTab.InlineCenteredSuccessText}>{(totalProductRevenu).toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                        : <Text className={stylesTab.InlineCenteredDangerText}>{(totalProductRevenu).toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                    }
                </div>
            </div>
        }
    </div>;
};